/**
 * Shared submit helpers — toast on failure, refetch /status on success.
 *
 * Belongs here: try/catch wrappers around POST + status refresh.
 * Does not belong here: status fetch implementation (see useQuestionnaireStatusQuery).
 */
import type { QueryClient } from '@tanstack/react-query'
import { refetchQuestionnaireStatusWithRetries } from './useQuestionnaireStatusQuery'

export const EXPERT_REVIEW_SAVE_ERROR_TOAST = 'Could not save your changes. Please try again.'
export const EXPERT_REVIEW_STATUS_REFRESH_ERROR_TOAST = 'failed to refetch status'

type ShowToast = (options: { message: string; duration?: number }) => void

/**
 * Run a submit POST; on success refetch /status (with retries).
 * On failure: toast + rethrow unless `silent` (review background submit).
 */
export async function runExpertReviewSubmit(
  submit: () => Promise<unknown>,
  {
    queryClient,
    showToast,
    silent = false
  }: {
    queryClient: QueryClient
    showToast: ShowToast
    /** Background review submit — no toast; still rethrows so versions stay out of sync. */
    silent?: boolean
  }
): Promise<void> {
  try {
    await submit()
    await refetchQuestionnaireStatusWithRetries(queryClient)
  } catch (error) {
    if (!silent) {
      showToast({ message: EXPERT_REVIEW_SAVE_ERROR_TOAST, duration: 2500 })
    }
    throw error
  }
}
